import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKillSwitch } from '@/hooks/useKillSwitch';
import { GlobalBanner } from '@/components/notifications';

interface KillSwitchBannerProps {
  className?: string;
}

export default function KillSwitchBanner({ className }: KillSwitchBannerProps) {
  const { killSwitchState } = useKillSwitch();

  if (!killSwitchState?.active) {
    return null;
  }

  const formatTime = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const profileText =
    killSwitchState.profile === 'hard'
      ? 'Hard Halt (orders cancelled)'
      : 'Soft Halt (new orders blocked)';

  return (
    <GlobalBanner
      className={className}
      tone={killSwitchState.scope === 'global' ? 'critical' : 'warning'}
      icon={<AlertTriangle className="h-5 w-5" />}
      title={`Trading Halted - ${killSwitchState.scope === 'global' ? 'ALL TENANTS' : 'Current Tenant'}`}
      description={
        <div className="space-y-2 text-sm">
          <p>
            {profileText} • Activated by {killSwitchState.by}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatTime(killSwitchState.at)}
            </span>
            {killSwitchState.reason ? <span>Reason: {killSwitchState.reason}</span> : null}
          </div>
          {killSwitchState.filters ? (
            <div className="space-y-1 text-xs text-slate-600">
              {killSwitchState.filters.exchanges ? (
                <p>Exchanges: {killSwitchState.filters.exchanges.join(', ')}</p>
              ) : null}
              {killSwitchState.filters.tags ? (
                <p>Tags: {killSwitchState.filters.tags.join(', ')}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      }
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open('/help/kill-switch', '_blank')}
          data-testid="button-kill-switch-help"
        >
          Help
        </Button>
      }
    />
  );
}
